import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheckCircle, faClock, faBan, faRotateLeft } from '@/icons';
import { CaseSentRecipient } from '../types/caseApi';
import { caseApiService } from '../services/caseApiService';
import { useAlert } from '../contexts/AlertContext';
import { CustomToggle } from './CustomToggle';

interface RescindRecipientsModalProps {
    isOpen: boolean;
    caseSentId: string;
    recipients: CaseSentRecipient[];
    onClose: () => void;
    onRescind: () => void;
    onRescindError?: (message: string) => void;
}

export const RescindRecipientsModal: React.FC<RescindRecipientsModalProps> = ({
    isOpen,
    caseSentId,
    recipients,
    onClose,
    onRescind,
    onRescindError,
}) => {
    const theme = useTheme();
    const { addAlert } = useAlert();
    const [rescinding, setRescinding] = useState(false);
    const [selectedRecipients, setSelectedRecipients] = useState<Set<string>>(new Set());
    const [cancelNote, setCancelNote] = useState('');

    useEffect(() => {
        if (isOpen && recipients.length > 0) {
            // Pre-select all pending recipients (toggles ON by default)
            const pendingIds = recipients
                .filter(r => r.status === 'Pending' && r.caseReceivedId)
                .map(r => r.caseReceivedId!);
            setSelectedRecipients(new Set(pendingIds));
        } else {
            setSelectedRecipients(new Set());
            setCancelNote('');
        }
    }, [isOpen, recipients]);

    const toggleRecipient = (caseReceivedId: string, status?: string) => {
        // Only allow toggling for Pending recipients
        if (status !== 'Pending') {
            return;
        }
        const newSelected = new Set(selectedRecipients);
        if (newSelected.has(caseReceivedId)) {
            newSelected.delete(caseReceivedId);
        } else {
            newSelected.add(caseReceivedId);
        }
        setSelectedRecipients(newSelected);
    };

    const handleRescind = async () => {
        if (selectedRecipients.size === 0) {
            addAlert('error', 'Please select at least one recipient to rescind');
            return;
        }

        setRescinding(true);
        try {
            const response = await caseApiService.rescindFile(
                caseSentId,
                cancelNote || undefined,
                Array.from(selectedRecipients)
            );
            if (response.success) {
                addAlert('success', response.message || 'Recipients rescinded successfully');
                onRescind();
                onClose();
            } else {
                // Check if this is the special error code for already accepted cases
                if (response.errorCode === 'CASE-ALREADY-ACCEPTED') {
                    // Pass the error to the parent to show modal
                    onRescindError?.(response.message || 'This case cannot be rescinded because it has already been accepted');
                } else {
                    addAlert('error', response.message || 'Failed to rescind recipients');
                }
            }
        } catch (error: any) {
            console.error('Error rescinding recipients:', error);
            // Check if error details contain the special error code
            if (error?.details?.errorCode === 'CASE-ALREADY-ACCEPTED') {
                // Pass the error to the parent to show modal
                onRescindError?.(error.details.message || 'This case cannot be rescinded because it has already been accepted');
            } else {
                addAlert('error', error?.details?.message || error?.message || 'Failed to rescind recipients');
            }
        } finally {
            setRescinding(false);
        }
    };

    const getStatusIcon = (status?: string) => {
        switch (status) {
            case 'Accepted':
                return <FontAwesomeIcon icon={faCheckCircle} style={{ color: theme.colors.status?.success || '#10b981' }} />;
            case 'Pending':
                return <FontAwesomeIcon icon={faClock} style={{ color: theme.colors.status?.warning || '#f59e0b' }} />;
            case 'Rejected':
                return <FontAwesomeIcon icon={faBan} style={{ color: theme.colors.status?.error || '#dc2626' }} />;
            case 'Rescinded':
                return <FontAwesomeIcon icon={faRotateLeft} style={{ color: theme.colors.text?.secondary || '#6b7280' }} />;
            default:
                return <FontAwesomeIcon icon={faClock} style={{ color: theme.colors.text?.secondary || '#6b7280' }} />;
        }
    };

    if (!isOpen) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
        }}>
            <div style={{
                backgroundColor: (theme.colors.background as any)?.primary || '#ffffff',
                borderRadius: '8px',
                padding: '24px',
                maxWidth: '600px',
                width: '90%',
                maxHeight: '80vh',
                overflow: 'auto',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px',
                }}>
                    <h2 style={{
                        margin: 0,
                        fontSize: theme.typography?.fontSize?.lg || '20px',
                        fontWeight: 'bold',
                        color: theme.colors.text?.primary || '#000000',
                    }}>
                        Select Recipients to Rescind
                    </h2>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '20px',
                            color: theme.colors.text?.secondary || '#6b7280',
                            padding: '4px 8px',
                        }}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div style={{ marginBottom: '24px' }}>
                    <h3 style={{
                        fontSize: theme.typography?.fontSize?.md || '16px',
                        fontWeight: '600',
                        marginBottom: '12px',
                        color: theme.colors.text?.primary || '#000000',
                    }}>
                        Select Recipients to Rescind
                    </h3>
                    <div style={{
                        border: `1px solid ${theme.colors.border?.default || '#e5e7eb'}`,
                        borderRadius: '4px',
                        overflow: 'hidden',
                    }}>
                        {recipients.map((recipient) => {
                            const isPending = recipient.status === 'Pending';
                            const isAccepted = recipient.status === 'Accepted';
                            const isRejected = recipient.status === 'Rejected';
                            const canToggle = isPending;
                            const isSelected = selectedRecipients.has(recipient.caseReceivedId || '');

                            return (
                                <div
                                    key={recipient.caseReceivedId}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '12px',
                                        borderBottom: `1px solid ${theme.colors.border?.default || '#e5e7eb'}`,
                                        backgroundColor: isSelected
                                            ? (theme.colors.background as any)?.secondary || '#f3f4f6'
                                            : 'transparent',
                                        opacity: canToggle ? 1 : 0.6,
                                    }}
                                >
                                    <div style={{ flex: 1 }}>
                                        <div style={{
                                            fontWeight: '500',
                                            color: theme.colors.text?.primary || '#000000',
                                        }}>
                                            {recipient.userName || 'Unknown User'}
                                        </div>
                                        <div style={{
                                            fontSize: theme.typography?.fontSize?.sm || '14px',
                                            color: theme.colors.text?.secondary || '#6b7280',
                                        }}>
                                            {recipient.teamName || 'Unknown Team'}
                                        </div>
                                    </div>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                    }}>
                                        <CustomToggle
                                            checked={isSelected}
                                            onChange={() => recipient.caseReceivedId && toggleRecipient(recipient.caseReceivedId, recipient.status)}
                                            disabled={!canToggle}
                                            label=""
                                            style={{
                                                padding: 0,
                                                border: 'none',
                                                backgroundColor: 'transparent',
                                            }}
                                        />
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            color: theme.colors.text?.secondary || '#6b7280',
                                            minWidth: '100px',
                                        }}>
                                            {getStatusIcon(recipient.status)}
                                            <span style={{ fontSize: theme.typography?.fontSize?.sm || '14px' }}>
                                                {recipient.status || 'Pending'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: theme.typography?.fontSize?.sm || '14px',
                        fontWeight: '500',
                        color: theme.colors.text?.primary || '#000000',
                    }}>
                        Cancel Note (Optional)
                    </label>
                    <textarea
                        value={cancelNote}
                        onChange={(e) => setCancelNote(e.target.value)}
                        placeholder="Enter a note about why you're rescinding..."
                        style={{
                            width: '100%',
                            minHeight: '80px',
                            padding: '8px',
                            border: `1px solid ${theme.colors.border?.default || '#e5e7eb'}`,
                            borderRadius: '4px',
                            fontSize: theme.typography?.fontSize?.sm || '14px',
                            fontFamily: 'inherit',
                            resize: 'vertical',
                        }}
                    />
                </div>

                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '12px',
                }}>
                    <button
                        onClick={onClose}
                        disabled={rescinding}
                        style={{
                            padding: '8px 16px',
                            border: `1px solid ${theme.colors.border?.default || '#e5e7eb'}`,
                            borderRadius: '4px',
                            backgroundColor: (theme.colors.background as any)?.primary || '#ffffff',
                            color: theme.colors.text?.primary || '#000000',
                            cursor: rescinding ? 'not-allowed' : 'pointer',
                            fontSize: theme.typography?.fontSize?.sm || '14px',
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleRescind}
                        disabled={rescinding || selectedRecipients.size === 0}
                        style={{
                            padding: '8px 16px',
                            border: 'none',
                            borderRadius: '4px',
                            backgroundColor: rescinding || selectedRecipients.size === 0
                                ? theme.colors.status?.error + '80' || '#dc262680'
                                : theme.colors.status?.error || '#dc2626',
                            color: '#ffffff',
                            cursor: rescinding || selectedRecipients.size === 0 ? 'not-allowed' : 'pointer',
                            fontSize: theme.typography?.fontSize?.sm || '14px',
                            fontWeight: '500',
                        }}
                    >
                        {rescinding ? 'Rescinding...' : `Rescind ${selectedRecipients.size} Recipient${selectedRecipients.size !== 1 ? 's' : ''}`}
                    </button>
                </div>
            </div>
        </div>
    );
};
